import { motion } from 'framer-motion';
import React, { useEffect } from 'react';

import { useApp } from '../../context/AppContext';

import BlogList from './BlogList';
import styles from './ContentRing.module.css';
import IconRing from './IconRing';
import ShopGallery from './ShopGallery';
import TextCloud from './TextCloud';

/**
 * ContentRing Component
 *
 * Displays content in the space between the circular menu and viewport edge.
 * Routes content to appropriate display component based on content type:
 * - text_cloud: TextCloud component (About Me)
 * - image_gallery: ShopGallery component with category filtering (Portfolio)
 * - shop_gallery: ShopGallery component with pricing (Shop)
 * - icon_ring: IconRing component (Contacts, Courses)
 */
const ContentRing: React.FC = () => {
  const { activeMenuItem, content, loadContent, isLoadingContent, contentError } = useApp();

  // Load content when active menu item changes
  useEffect(() => {
    if (activeMenuItem) {
      loadContent(activeMenuItem);
    }
  }, [activeMenuItem, loadContent]);

  /**
   * Render content based on type
   */
  const renderContent = () => {
    if (!content) return null;

    switch (content.type) {
      case 'text-cloud': {
        // About Me page has two text clouds (personal and professional info)
        const textContent = content.content as {
          readonly personalInfo?: string;
          readonly professionalInfo?: string;
          readonly personalTitle?: string;
          readonly professionalTitle?: string;
        };
        return (
          <>
            {textContent.personalInfo !== undefined && (
              <TextCloud
                content={{
                  text: textContent.personalInfo ?? 'Add your personal information here...',
                  title: textContent.personalTitle ?? 'Personal',
                }}
                position="top"
              />
            )}
            {textContent.professionalInfo !== undefined && (
              <TextCloud
                content={{
                  text: textContent.professionalInfo ?? 'Add your professional information here...',
                  title: textContent.professionalTitle ?? 'Professional',
                }}
                position="bottom"
              />
            )}
          </>
        );
      }

      case 'image-gallery': {
        // Image gallery with circular layout and category filtering
        // Positioned outside center grid cell to avoid overlapping with menu
        // ShopGallery manages its own category selection state
        return <ShopGallery categoryId="all" radius={380} autoRotate={false} type="portfolio" />;
      }

      case 'icon-ring': {
        // Icon ring with external links
        // Positioned outside center grid cell to avoid overlapping with menu
        const iconContent = content.content as {
          readonly items?: ReadonlyArray<{
            readonly label: string;
            readonly icon: string;
            readonly url: string;
            readonly order: number;
          }>;
        };
        return <IconRing content={iconContent} radius={380} />;
      }

      case 'shop-gallery': {
        // Shop gallery with pricing information and category filtering
        // Positioned outside center grid cell to avoid overlapping with menu
        // ShopGallery manages its own category selection state
        return <ShopGallery categoryId="all" radius={380} autoRotate={false} type="shop" />;
      }

      case 'blog': {
        // Blog page with list of posts
        const blogContent = content.content as {
          readonly description?: string;
          readonly settings?: {
            readonly postsPerPage?: number;
            readonly showExcerpts?: boolean;
            readonly showFeaturedImages?: boolean;
          };
        };
        return (
          <>
            {blogContent.description && (
              <TextCloud
                content={{
                  text: blogContent.description,
                  title: content.metadata?.title ?? 'Blog',
                }}
                position="top"
                fixed={true}
              />
            )}
            <BlogList pageSlug={content.slug} settings={blogContent.settings} />
          </>
        );
      }

      default:
        return (
          <div className={styles.error}>
            <p>Unknown content type: {content.type}</p>
          </div>
        );
    }
  };

  // Loading state
  if (isLoadingContent) {
    return (
      <div className={styles.contentRing}>
        <motion.div
          className={styles.loading}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className={styles.loadingSpinner}></div>
          <p>Loading content...</p>
        </motion.div>
      </div>
    );
  }

  // Error state
  if (contentError) {
    return (
      <div className={styles.contentRing}>
        <motion.div
          className={styles.error}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <p>Error loading content</p>
          <p className={styles.errorMessage}>{contentError}</p>
        </motion.div>
      </div>
    );
  }

  // No content state
  if (!content) {
    return (
      <div className={styles.contentRing}>
        <motion.div
          className={styles.empty}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <p>No content available</p>
        </motion.div>
      </div>
    );
  }

  // Render content with animation
  return (
    <motion.div
      className={styles.contentRing}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      key={activeMenuItem} // Re-animate when content changes
    >
      {/* Render page content */}
      {renderContent()}
    </motion.div>
  );
};

export default ContentRing;
